import React, { useState, useRef, useEffect } from "react";
import styles from "./SelectCarrier.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import NewModal from "../NewModal/NewModal";
import { useNavigate, useLocation } from "react-router-dom";
import decodeToken from "../../lib/decodeToken";
import Loader2 from "../Loader2/Loader2";
import ForwardingConditionModal from "./ForwardingConditionModal";
import { onDeviceCarrier } from "../../lib/deviceCarrier";
import {
  readStoredConditions,
  writeStoredConditions,
} from "../../lib/callForwardingCodes";
import { usesVobiz } from "../../utils/apiBackend";


// Carriers supported for carrier-side call forwarding. Kept in sync with
// the callForwardingConfig map in ForwardInstructions.js — if you add
// a carrier here, add the activation/deactivation codes there too.
// MVNOs inherit their parent network's dial codes (Cricket/Boost/Metro
// → T-Mobile GSM codes; Visible/Xfinity/Spectrum → Verizon CDMA codes,
// etc.).
// 🇮🇳 India — the only networks worth listing on the India site. All GSM, so
// they share the standard MMI codes (see GSM_CARRIERS in callForwardingCodes).
const IN_CATEGORIES = ["Jio", "Airtel", "Vi", "BSNL"];

const US_CA_CATEGORIES = [
  // 🇺🇸 US — major networks
  "AT&T",
  "Verizon",
  "T-Mobile US",
  "U.S. Cellular",

  // 🇺🇸 US — common MVNOs
  "Cricket Wireless",
  "Metro by T-Mobile",
  "Boost Mobile",
  "Mint Mobile",
  "Visible",
  "Xfinity Mobile",
  "Spectrum Mobile",
  "Consumer Cellular",
  "Google Fi",

  // 🇨🇦 Canada — major networks
  "Rogers",
  "Bell",
  "Telus",
  "Freedom Mobile",

  // 🇨🇦 Canada — subsidiaries / flanker brands
  "Fido",
  "Koodo",
  "Virgin Plus",
  "Chatr Mobile",
  "Lucky Mobile",
  "Public Mobile",

  // 🇮🇳 India — staging only (added dynamically below)
  ...(process.env.REACT_APP_ENV === "staging"
    ? ["Jio", "Airtel"]
    : []),
];

// The India site sold an Indian number and then asked the owner to pick between
// AT&T and Verizon. Same domain switch the number-purchase screens run on.
const categories = usesVobiz() ? IN_CATEGORIES : US_CA_CATEGORIES;

const SelectCarrier = ({
  setIsFocused = () => { },
  // Wizard integration — when true, the component hands off to the
  // provided callbacks instead of its own navigate() calls. Default
  // behavior (standalone /select-carrier route) is untouched.
  wizardMode = false,
  onWizardNext,
  onWizardBack,
  onWizardClose,
  /*
   * GoLiveWizard footer redesign — when true, the bottom helper
   * "We'll show simple steps to forward calls." + divider stack is
   * REPLACED with the Figma's publish-directly skip pattern:
   *
   *   I will publish ✓ {agentName}'s number directly
   *   Skip this step →
   *
   * AND the primary CTA label flips from "Continue" to
   * "Show me Steps". onWizardSkipPublishDirectly fires when the
   * user taps "Skip this step →" — GoLiveWizard wires this to
   * goSkip (which jumps to Triage, bypassing Forwarding).
   *
   * PostPurchaseSetup doesn't pass this prop (default false) so its
   * Carrier screen renders with the legacy footer untouched.
   */
  wizardSkipPublishDirectly = false,
  onWizardSkipPublishDirectly,
  agentName: agentNameProp = "",
  /*
   * Conditional call forwarding (GoLive wizard only). When true, Continue
   * opens the "When Call Forwarding?" sheet instead of advancing straight
   * away; the picked conditions are handed to onWizardNext as its 2nd arg:
   *
   *   onWizardNext(carrier, ["busy", "unanswered"])
   *
   * Returning a promise from onWizardNext keeps the sheet in its "Saving…"
   * state until the backend write settles. PostPurchaseSetup and the
   * standalone /select-carrier route leave this off (default false) and
   * behave exactly as before.
   */
  conditionPrompt = false,
  onConditionPromptShown,
}) => {
  // Resolved agent display name — fallback chain ends at "Sophia"
  // (the default seed name new agents get at creation).
  const agentName = agentNameProp || "Sophia";
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const selectedItemRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showList, setShowList] = useState(false);
  const [selected, setSelected] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const token = localStorage.getItem("token") || "";
  const { id: userId } = decodeToken(token) || {};
  // Prefer ?agentId= from the URL — lifecycle email / deep-link CTAs land here
  // cold (no sessionStorage set yet). Falls back to the session-selected agent.
  const queryAgentId = new URLSearchParams(location.search).get("agentId");
  const agentId = queryAgentId || sessionStorage.getItem("SelectAgentId");
  const [dashboardLoading, setDashboardLoading] = useState(false);
  // "When Call Forwarding?" sheet — only reachable when conditionPrompt is on.
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [savingConditions, setSavingConditions] = useState(false);
  const [pickedConditions, setPickedConditions] = useState(() =>
    readStoredConditions(),
  );

  useEffect(() => {
    if (queryAgentId) sessionStorage.setItem("SelectAgentId", queryAgentId);
  }, [queryAgentId]);



  // Persist an agentId that arrived via the URL so downstream steps
  // (ForwardInstructions) that read SelectAgentId from session keep working.
  useEffect(() => {
    if (queryAgentId) sessionStorage.setItem("SelectAgentId", queryAgentId);
  }, [queryAgentId]);

  // Session storage se data load karo on mount
  useEffect(() => {
    const savedCarrier = sessionStorage.getItem("selectedCarrier");
    if (savedCarrier) {
      setSelected(savedCarrier);
    }
  }, []);

  /*
   * Native shell ne device ka carrier bataya ho to use PRESELECT kar do.
   *
   * Ye tabhi chalta hai jab user ne khud pehle koi carrier na chuna ho — uski
   * apni pasand par kabhi nahi likhte.
   *
   * Naam hu-ba-hu match nahi hota ("T-Mobile" vs "T-Mobile US", "Verizon
   * Wireless" vs "Verizon"), isliye dono taraf se `includes` dekhte hain aur
   * list me se sabse LAMBA match lete hain — warna "AT&T" jaisa chhota naam
   * galti se jeet jaata hai.
   *
   * onDeviceCarrier subscription hai (poll nahi): native ka jawab is screen ke
   * mount hone se pehle bhi aa sakta hai aur baad me bhi — dono case chalte hain.
   */
  useEffect(() => {
    const unsubscribe = onDeviceCarrier((carrier) => {
      if (!carrier) return;
      try {
        if (sessionStorage.getItem("selectedCarrier")) return; // user ki pasand
      } catch {
        /* storage blocked — aage badho */
      }
      const needle = carrier.toLowerCase();
      const match = categories
        .filter((c) => {
          const n = c.toLowerCase();
          return n.includes(needle) || needle.includes(n);
        })
        .sort((a, b) => b.length - a.length)[0];
      if (!match) return;
      setSelected(match);
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter by search, then move the selected carrier to the top so it's
  // immediately visible — remaining carriers stay in their original order.
  const filteredList = (() => {
    const matched = categories.filter((item) =>
      item.toLowerCase().includes(search.toLowerCase())
    );
    if (!selected || !matched.includes(selected)) return matched;
    return [selected, ...matched.filter((item) => item !== selected)];
  })();

  // Auto-scroll the carrier list so the selected item stays visible.
  // Fires whenever `selected` changes — covers session-restore, native
  // carrier preselect, and manual taps when the item is off-screen.
  useEffect(() => {
    if (selected && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selected]);

  const handleFocus = () => {
    // Carrier already selected → clear search text so the FULL list
    // reappears (the selected item stays highlighted via its radio state).
    if (selected) setSearch("");

    setShowList(true);
    setIsFocused(true);
    setIsInputFocused(true);

    setTimeout(() => {
      if (searchRef.current) {
        const top =
          searchRef.current.getBoundingClientRect().top +
          window.pageYOffset -
          16;

        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 100);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowList(false);
      setIsFocused(false);
      setIsInputFocused(false);
    }, 150);
  };

  // localStorage mirror — sessionStorage browser-tab close pe wipe ho
  // jata hai, jiski wajah se ForwardInstructions step pe carrier
  // missing dikha aur fallback `*72` (Verizon CDMA) default fire ho
  // gaya for non-Verizon users → galat dialer code. localStorage
  // scoped by agentId so multi-tab + multi-agent flows me leak nahi.
  const carrierKeyFor = (id) => (id ? `postPurchase.carrier.${id}` : "");

  const handleSelect = (value) => {
    setSelected(value);
    setSearch("");
    setShowList(false);
    setIsFocused(false);
    setIsInputFocused(false);

    // Session storage mein save karo
    sessionStorage.setItem("selectedCarrier", value);
    // Mirror to localStorage (browser-close survival)
    try {
      const k = carrierKeyFor(agentId);
      if (k) localStorage.setItem(k, value);
    } catch { }

    // GoLive wizard — the sheet opens on SELECT, not on Continue. Tapping a
    // carrier is already the decisive action; making them hunt for the
    // button first just adds a step.
    if (conditionPrompt) {
      setShowConditionModal(true);
      if (typeof onConditionPromptShown === "function") {
        onConditionPromptShown(value);
      }
    }
  };

  const handleContinue = () => {
    if (!selected) return;

    // Session storage mein save karo (agar pehle se nahi hai to)
    sessionStorage.setItem("selectedCarrier", selected);
    try {
      const k = carrierKeyFor(agentId);
      if (k) localStorage.setItem(k, selected);
    } catch { }

    // GoLive wizard — the sheet already opened on select; this reopens it
    // for anyone who dismissed it with the ✕ and then tapped Continue.
    if (conditionPrompt) {
      setShowConditionModal(true);
      return;
    }

    if (wizardMode && typeof onWizardNext === "function") {
      onWizardNext(selected);
      return;
    }

    navigate("/forward-instructions", {
      state: { carrier: selected },
    });
  };

  // Sheet's Continue — persist the selection, then hand off to the wizard.
  // The sheet stays open (in "Saving…") while onWizardNext's promise runs so
  // the user never sees a half-written state.
  const handleConditionsContinue = async (conditions) => {
    setPickedConditions(conditions);
    writeStoredConditions(conditions);

    if (!(wizardMode && typeof onWizardNext === "function")) {
      setShowConditionModal(false);
      navigate("/forward-instructions", {
        state: { carrier: selected, conditions },
      });
      return;
    }

    setSavingConditions(true);
    try {
      await onWizardNext(selected, conditions);
    } catch (e) {
      // Backend write failed — the selection is already in sessionStorage so
      // the next screen still renders the right codes. Don't block the user.
      console.error("Failed to save forwarding conditions:", e);
    } finally {
      setSavingConditions(false);
      setShowConditionModal(false);
    }
  };
  const handleGoDashboard = async () => {
    if (dashboardLoading) return;

    setDashboardLoading(true);

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/notifications/call-forwarding-abandon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          agentId,
          resumeUrl: `${window.location.origin}/select-carrier`,
        }),
      });
    } catch (e) {
      console.error("call-forwarding-abandon trigger failed:", e);
    } finally {
      setDashboardLoading(false);
      if (wizardMode && typeof onWizardClose === "function") {
        onWizardClose();
        return;
      }
      navigate("/fast-agent-detail", { replace: true });
    }
  };
  return (
    <>
      {dashboardLoading && (
        <div className={styles.loadingOverlay}>
          <Loader2 />
        </div>
      )}

      <div
        className={`${styles.containerBox}${wizardMode ? ` ${styles.containerBoxWizard}` : ""}`}
      // Default CSS has min-height: 100dvh + absolute-positioned CTAs
      // which push past the viewport when hosted inside the wizard.
      // In wizardMode we flow everything naturally (see overrides below).
      >
        <div className={styles.container}>
          <div className={styles.titleBar}>
            <h4 className={styles.lightTitle}>Who is your</h4>
            <h5 className={styles.boldTitle}> carrier?</h5>
          </div>

          <div className={styles.searchBox} ref={searchRef}>
            {!search && <img src="/svg/Search-Icon.svg" alt="Search" />}
            <input
              placeholder={
                usesVobiz()
                  ? "Search your carrier, like Jio or Airtel"
                  : "Search your carrier, like AT&T or Verizon"
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* {showList && ( */}
          <div className={styles.list}>
            {filteredList.length ? (
              filteredList.map((item, i) => (
                <label
                  key={i}
                  className={styles.listItem}
                  ref={selected === item ? selectedItemRef : null}
                >
                  <input
                    type="radio"
                    name="carrier"
                    checked={selected === item}
                    onChange={() => handleSelect(item)}
                  />
                  <span className={styles.radio} onChange={() => handleSelect(item)} />
                  <span className={styles.text}>{item}</span>
                </label>
              ))
            ) : (
              <div className={styles.noResult}>No data found</div>
            )}
          </div>
          {/* )} */}
        </div>

        {/*
           Legacy bottombar (helper text + divider) — only renders when
           the GoLive publish-directly pattern is OFF. With the new
           pattern enabled the helper line + skip live BELOW the CTA
           instead, matching the Forwarding screen's footer rhythm.
        */}
        {!wizardSkipPublishDirectly && (
          <div
            // wizardMode: pin the helper text + divider directly above the
            // fixed Continue button (which sits at bottom: 64px) so it
            // doesn't float up next to the carrier list when the list is
            // short. Keeps the visual rhythm: text → divider → button.
            className={`${styles.bottombar}${wizardMode ? ` ${styles.bottombarWizard}` : ""}`}
          >
            <p className={styles.desc}>We'll show simple steps to forward calls.</p>
            <div className={styles.divider}></div>
          </div>
        )}

        <div
          /* In publish-directly mode the legacy helper bottombar is
             gone, so we drop the CTA closer to the viewport bottom
             (24px) — the publish line + "Skip this step →" link
             render BELOW the button inside this same fixed cluster
             and end ~24px above the viewport bottom. Default mode
             keeps the original 64px offset (helper bottombar sits
             at 130px above it). */
          className={`${styles.looksGoodBtn}${wizardMode ? ` ${styles.looksGoodBtnWizard}` : ""}${wizardMode && wizardSkipPublishDirectly ? ` ${styles.looksGoodBtnWizardSkip}` : ""}`}
        >
          <AnimatedButton
            label={wizardSkipPublishDirectly ? "Show Call Forwarding Steps" : "Continue"}
            onClick={handleContinue}
            disabled={!selected}
            positionType={wizardMode ? "relative" : undefined}
          />
          {/*
             Publish-directly skip pattern — sits directly under the
             primary CTA, mirrors the Forwarding screen footer so the
             two visual-Step-2 screens (Carrier + Forwarding) feel like
             one continuous flow. Tapping "Skip this step →" calls
             onWizardSkipPublishDirectly which the wizard wires to
             goSkip → setStepId(4) (Triage), bypassing the Forwarding
             screen entirely.
          */}
          {wizardSkipPublishDirectly && (
            <div className={styles.publishWrap}>
              <span className={styles.publishText}>
                Use {agentName} 's number as my public number instead
              </span>
              <button
                type="button"
                onClick={() => {
                  if (typeof onWizardSkipPublishDirectly === "function") {
                    onWizardSkipPublishDirectly();
                  }
                }}
                className={styles.skipStepBtn}
              >
               Skip call forwarding 
                <span aria-hidden="true">→</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {!wizardMode && (
        <div className={styles.crossIcon} onClick={() => setIsModalOpen(true)}>
          <img src="/svg/cross_icon_gray.svg" alt="cross_icon_gray" />
        </div>
      )}

      <ForwardingConditionModal
        isOpen={showConditionModal}
        onClose={() => setShowConditionModal(false)}
        onContinue={handleConditionsContinue}
        initialConditions={pickedConditions}
        isSaving={savingConditions}
        carrier={selected}
        agentName={agentName}
      />

      <NewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="You can finish this later"
        height="auto"
      >
        <div className={styles.ModalBody}>
          <div className={styles.worriesDiv}>
            <span className={styles.dash}>—</span>
            <strong>No worries</strong>
            <span className={styles.dash}>—</span>
            <p>you can set up call forwarding anytime.</p>
          </div>

          <p className={styles.description}>
            For now, you can continue to your{" "}
            <b className={styles.bolddesc}>dashboard.</b>
          </p>

          <div className={styles.CallForwarding}>
            <AnimatedButton
              label="Go to Dashboard"
              positionType="relative"
              width="auto"
              bottom="0px"
              onClick={handleGoDashboard}
            />

          </div>
        </div>
      </NewModal>
    </>
  );
};

export default SelectCarrier;